import { billingAccessLockLabel, getProjectBillingAccess } from "./projectBillingAccess";
import {
  isProjectCapabilityEnabled,
  normalizeProjectBusinessProfile,
  projectBusinessGoals,
  projectStarterAreas,
} from "./projectCapabilities";
import { normalizeProjectModules } from "./projectModules";
import {
  hasAnyProjectPermission,
  hasProjectPermission,
  hasProjectRole,
  normalizeProjectAccess,
} from "./projectAccess";

export const DASHBOARD_NAVIGATION_SCOPES = Object.freeze({
  GLOBAL: "global",
  PROJECT: "project",
});

export const DASHBOARD_TOOL_ACCESS_STATES = Object.freeze({
  AVAILABLE: "available",
  LOCKED: "locked",
  HIDDEN: "hidden",
});

export const DASHBOARD_TOOL_ACCESS_REASONS = Object.freeze({
  AVAILABLE: "available",
  BILLING: "billing",
  PROJECT_STATUS: "project_status",
  MODULE_OR_CAPABILITY: "module_or_capability",
  PERMISSION: "permission",
  ROLE: "role",
});

export const DASHBOARD_GLOBAL_GROUP_KEYS = Object.freeze({
  WORKSPACE: "workspace",
  ADMINISTRATION: "administration",
  ACCOUNT: "account",
});

export const DASHBOARD_ACCOUNT_CONTEXT = Object.freeze({
  key: "account",
  label: "Cuenta",
  scope: DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
  requiresProject: false,
});

export const DASHBOARD_AREA_KEYS = Object.freeze({
  HOME: "home",
  PAGE: "page",
  SALES: "sales",
  CUSTOMERS: "customers",
  OPERATION: "operation",
  GASTRONOMY: "gastronomy",
  MANAGEMENT: "management",
  STATS: "stats",
  SETTINGS: "settings",
  MORE: "more",
  ACCOUNT: "account",
  ADMINISTRATION: "administration",
});

export const DASHBOARD_AREAS = Object.freeze([
  {
    key: DASHBOARD_AREA_KEYS.HOME,
    label: "Inicio",
    iconKey: "home",
    description: "Resumen y próximos pasos del panel.",
  },
  {
    key: DASHBOARD_AREA_KEYS.PAGE,
    label: "Página",
    iconKey: "page",
    description: "Sitio público y edición de la presencia web.",
  },
  {
    key: DASHBOARD_AREA_KEYS.SALES,
    label: "Ventas",
    iconKey: "orders",
    description: "Catálogo, pedidos, ventas registradas y cobros online en un mismo circuito comercial.",
  },
  {
    key: DASHBOARD_AREA_KEYS.CUSTOMERS,
    label: "Clientes",
    iconKey: "customers",
    description: "CRM, clientes y funciones asociadas como membresías.",
  },
  {
    key: DASHBOARD_AREA_KEYS.OPERATION,
    label: "Operación",
    iconKey: "agenda",
    description: "Turnos, agenda y reservas de servicios según el negocio.",
  },
  {
    key: DASHBOARD_AREA_KEYS.GASTRONOMY,
    label: "Gastronomía",
    iconKey: "restaurant",
    description: "Carta, mesas, reservas de mesa, comandas y recetas activas.",
  },
  {
    key: DASHBOARD_AREA_KEYS.MANAGEMENT,
    label: "Gestión",
    iconKey: "management",
    description: "Caja, movimientos, inventario y contabilidad del negocio.",
  },
  {
    key: DASHBOARD_AREA_KEYS.STATS,
    label: "Estadísticas",
    iconKey: "stats",
    description: "Rendimiento web y señales del negocio cuando el módulo está activo.",
  },
  {
    key: DASHBOARD_AREA_KEYS.SETTINGS,
    label: "Configuración",
    iconKey: "settings",
    description: "Datos del negocio, plan, capacidades y accesos del proyecto.",
  },
  {
    key: DASHBOARD_AREA_KEYS.MORE,
    label: "Más",
    iconKey: "more",
    description: "Actividad, notificaciones y herramientas realmente secundarias.",
  },
  {
    key: DASHBOARD_AREA_KEYS.ACCOUNT,
    label: "Cuenta",
    iconKey: "account",
    description: "Proyectos, red de referidos y seguridad de la cuenta WebProfit.",
    globalOnly: true,
  },
  {
    key: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Administración",
    iconKey: "superpanel",
    description: "Herramientas globales exclusivas de superadministración.",
    globalOnly: true,
  },
]);

export const PROJECT_SETTINGS_SECTION_DEFINITIONS = Object.freeze([
  {
    value: "general",
    label: "Negocio",
    mobileLabel: "Negocio",
    title: "Negocio",
    description: "Perfil, objetivos y capacidades que definen cómo opera el proyecto.",
    iconKey: "settings",
    billingSensitive: true,
    permissions: Object.freeze(["project.view"]),
  },
  {
    value: "pagina",
    label: "Página y dominio",
    mobileLabel: "Página",
    title: "Página y dominio",
    description: "Publicación, dominio WebProfit y dominio personalizado.",
    iconKey: "page",
    requiresModule: "landing",
    permissions: Object.freeze(["project.view"]),
  },
  {
    value: "ecommerce",
    label: "Cobros online",
    mobileLabel: "Cobros",
    title: "Cobros online",
    description: "Medios de pago que usa el comercio para cobrar a sus clientes.",
    iconKey: "payments",
    requiresModule: "ecommerce",
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin"]),
    // Vive técnicamente bajo /configuracion por compatibilidad, pero su dueño
    // conceptual es Ventas. No dejamos que Configuración reclame esta ruta.
    hideFromSettingsNavigation: true,
  },
  {
    value: "facturacion",
    label: "Facturación ARCA",
    mobileLabel: "ARCA",
    title: "Facturación ARCA",
    description: "Credenciales fiscales, punto de venta y conexión con ARCA.",
    iconKey: "billing",
    requiresModule: "invoicing",
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin"]),
  },
  {
    value: "suscripcion",
    label: "Plan y facturación",
    mobileLabel: "Plan",
    title: "Plan y facturación",
    description: "Servicios contratados, mensualidad y medio de pago de WebProfit.",
    iconKey: "billing",
  },
  {
    value: "accesos",
    label: "Usuarios y permisos",
    mobileLabel: "Equipo",
    title: "Usuarios y permisos",
    description: "Miembros, roles, cajas y alcance de cada acceso.",
    iconKey: "permissions",
    billingSensitive: true,
    permissions: Object.freeze(["project.members.manage"]),
  },
  {
    value: "notificaciones",
    label: "Notificaciones",
    mobileLabel: "Alertas",
    title: "Notificaciones",
    description: "Avisos del proyecto y campañas para visitantes suscriptos.",
    iconKey: "notifications",
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin", "vendedor", "deposito", "facturacion", "viewer"]),
    hideFromSettingsNavigation: true,
  },
]);

const PROJECT_SETTINGS_ROUTE_SECTION_ALIASES = Object.freeze({
  "": "general",
  "/billing": "suscripcion",
  "/ecommerce": "ecommerce",
  "/notificaciones": "notificaciones",
  "/configuracion/funcionalidades": "suscripcion",
});

export const PROJECT_MANAGEMENT_PRIMARY_TOOLS = Object.freeze([
  {
    key: "overview",
    label: "Estado del negocio",
    shortLabel: "Resumen",
    helper: "Indicadores, alertas y próximos pasos",
    path: "",
    exact: true,
    iconKey: "management-overview",
    permissions: Object.freeze(["erp.overview.read"]),
  },
  {
    key: "items",
    label: "Ítems",
    shortLabel: "Ítems",
    helper: "Productos, servicios e insumos",
    path: "items",
    hideFromAreaNavigation: true,
    iconKey: "management-items",
    permissions: ({ search }) => dashboardSearchParam(search, "crear") === "1"
      ? ["items.read", "items.create"]
      : ["items.read"],
    permissionMode: "all",
  },
  {
    key: "movements",
    label: "Qué pasó",
    shortLabel: "Movimientos",
    helper: "Compras, ventas, ajustes y gastos",
    path: "movimientos",
    iconKey: "management-movements",
    permissions: Object.freeze(["erp.movements.read"]),
    access: ({ search, memberAccess }) => {
      if (dashboardSearchParam(search, "nuevo") !== "1") return true;
      const kind = dashboardSearchParam(search, "tipo").toLowerCase();
      const byKind = {
        sale: ["erp.sales.create", "erp.movements.create"],
        purchase: ["erp.purchases.create", "erp.movements.create"],
        expense: ["erp.expenses.create", "erp.movements.create"],
        internal_consumption: ["erp.internal_consumption.create", "erp.movements.create"],
        collection: ["erp.collections.create", "erp.movements.create"],
        payment: ["erp.payments.create", "erp.movements.create"],
        transfer: ["erp.payments.create", "erp.movements.create"],
        cash_in: ["cash.operations.in", "cash.operations.create", "erp.movements.create"],
        cash_out: ["cash.operations.out", "cash.operations.create", "erp.movements.create"],
        opening_balance: ["erp.stock.manage", "erp.movements.create"],
        stock_adjustment: ["erp.stock.manage", "erp.movements.create"],
      };
      const requiredAny = byKind[kind] || [
        "erp.movements.create",
        "erp.sales.create",
        "erp.purchases.create",
        "erp.expenses.create",
        "erp.internal_consumption.create",
        "erp.collections.create",
        "erp.payments.create",
        "cash.operations.create",
        "cash.operations.in",
        "cash.operations.out",
        "erp.stock.manage",
      ];
      return hasAnyProjectPermission(memberAccess, requiredAny);
    },
    active: ({ projectRelativePath, search }) => (
      normalizeDashboardPathname(projectRelativePath || "/") === "/sistema/movimientos" &&
      !isProjectSalesMovementRoute(projectRelativePath, search)
    ),
  },
  {
    key: "cash",
    label: "Caja",
    shortLabel: "Caja",
    mobileLabel: "Caja",
    helper: "Cajas, bancos, billeteras y sesiones de dinero",
    path: "caja",
    iconKey: "management-cash",
    permissions: Object.freeze([
      "cash.registers.read",
      "cash.sessions.read",
      "cash.movements.read",
    ]),
  },
  {
    key: "stock",
    label: "Inventario",
    shortLabel: "Stock",
    helper: "Existencias y disponibilidad real",
    path: "stock",
    iconKey: "management-stock",
    permissions: Object.freeze(["erp.stock.read"]),
  },
  {
    key: "accounting",
    label: "Contabilidad",
    shortLabel: "Contabilidad",
    mobileLabel: "Contabilidad",
    helper: "Libro, transacciones, cuentas e impuestos",
    path: "ledger",
    iconKey: "management-accounting",
    permissions: Object.freeze([
      "erp.ledger.read",
      "erp.transactions.read",
      "erp.tax_profiles.read",
    ]),
  },
]);

export const PROJECT_MANAGEMENT_ACCOUNTING_TOOLS = Object.freeze([
  {
    key: "ledger",
    label: "Libro contable",
    shortLabel: "Libro",
    mobileLabel: "Libro",
    path: "ledger",
    iconKey: "management-accounting",
    permissions: Object.freeze(["erp.ledger.read"]),
  },
  {
    key: "transactions",
    label: "Transacciones",
    shortLabel: "Transacciones",
    mobileLabel: "Mov. dinero",
    helper: "Cobros y pagos registrados",
    path: "transacciones",
    iconKey: "management-transactions",
    permissions: Object.freeze(["erp.transactions.read"]),
  },
  {
    key: "accounts",
    label: "Plan de cuentas",
    shortLabel: "Cuentas",
    mobileLabel: "Cuentas",
    path: "cuentas",
    iconKey: "management-accounts",
    permissions: Object.freeze(["erp.ledger.read"]),
  },
  {
    key: "billing",
    label: "Facturación",
    shortLabel: "Facturación",
    mobileLabel: "Facturación",
    helper: "ARCA, CAE y ventas pendientes",
    path: "facturacion",
    iconKey: "billing",
    requiresModule: "invoicing",
    roles: Object.freeze(["owner", "admin", "facturacion"]),
  },
  {
    key: "taxes",
    label: "Impuestos",
    shortLabel: "Impuestos",
    mobileLabel: "Impuestos",
    path: "impuestos",
    iconKey: "management-taxes",
    permissions: Object.freeze(["erp.tax_profiles.read"]),
  },
]);


export const PROJECT_CUSTOMER_TOOLS = Object.freeze([
  {
    key: "customers-crm",
    label: "CRM",
    mobileLabel: "CRM",
    shortLabel: "CRM",
    helper: "Clientes, contacto, estado y actividad comercial",
    iconKey: "customers",
    permissions: ({ search }) => dashboardSearchParam(search, "accion").toLowerCase() === "crear-cliente"
      ? ["customers.read", "customers.manage"]
      : ["customers.read"],
    permissionMode: "all",
  },
  {
    key: "customers-pricing",
    label: "Listas de precios",
    mobileLabel: "Precios",
    shortLabel: "Precios",
    helper: "Asignaciones y listas disponibles para clientes",
    iconKey: "products",
    matchSearchParams: Object.freeze({ vista: "precios" }),
    permissions: Object.freeze(["customers.read", "price_lists.read"]),
    permissionMode: "all",
  },
  {
    key: "customers-memberships",
    label: "Membresías",
    mobileLabel: "Membresías",
    shortLabel: "Membresías",
    helper: "Planes, vigencias y renovaciones vinculadas al CRM",
    iconKey: "billing",
    matchSearchParams: Object.freeze({ vista: "membresias" }),
    permissions: Object.freeze(["customers.read"]),
    permissionMode: "all",
    capability: "memberships",
  },
  {
    key: "customers-expirations",
    label: "Vencimientos",
    mobileLabel: "Vencimientos",
    shortLabel: "Vencimientos",
    helper: "Próximas a vencer y vencidas que requieren seguimiento",
    iconKey: "notifications",
    matchSearchParams: Object.freeze({ vista: "vencimientos" }),
    permissions: Object.freeze(["customers.read"]),
    capability: "memberships",
  },
]);

export const PROJECT_GASTRONOMY_TOOLS = Object.freeze([
  {
    key: "gastronomy-menu",
    label: "Carta",
    mobileLabel: "Carta",
    shortLabel: "Carta",
    helper: "Ítems publicados en la carta gastronómica",
    iconKey: "restaurant-menu",
    permissions: ({ search }) => dashboardSearchParam(search, "crear") === "1"
      ? ["items.read", "items.create"]
      : ["items.read"],
    permissionMode: "all",
    capability: "gastronomicMenu",
  },
  {
    key: "gastronomy-tables",
    label: "Mesas",
    mobileLabel: "Mesas",
    shortLabel: "Mesas",
    helper: "Plano y operación del salón",
    iconKey: "tables",
    permissions: ({ search }) => dashboardSearchParam(search, "accion").toLowerCase() === "crear-mesa"
      ? ["tables.read", "tables.manage"]
      : ["tables.read"],
    permissionMode: "all",
    capability: "tables",
  },
  {
    key: "gastronomy-reservations",
    label: "Reservas de mesa",
    mobileLabel: "Reservas",
    shortLabel: "Reservas",
    helper: "Reservas gastronómicas separadas de la agenda de servicios",
    iconKey: "reservations",
    roles: Object.freeze(["owner", "admin", "vendedor", "viewer", "facturacion"]),
    capability: "tableReservations",
  },
  {
    key: "gastronomy-kitchen",
    label: "Comandas",
    mobileLabel: "Comandas",
    shortLabel: "Comandas",
    helper: "Consumos en mesa, retiro y entrega sobre el motor de pedidos",
    iconKey: "kitchen",
    roles: Object.freeze(["owner", "admin", "administrativo", "vendedor", "cajero", "facturacion", "viewer"]),
    capability: "kitchenOrders",
    requiresModule: "orders",
  },
  {
    key: "gastronomy-recipes",
    label: "Recetas",
    mobileLabel: "Recetas",
    shortLabel: "Recetas",
    helper: "Ítems e insumos que componen la operación gastronómica",
    iconKey: "recipes",
    permissions: ({ search }) => dashboardSearchParam(search, "crear") === "1"
      ? ["items.read", "items.create"]
      : ["items.read"],
    permissionMode: "all",
    capability: "recipes",
    requiresItemsSurface: true,
  },
]);

function projectHasAccessibleGastronomy(project, modules = {}, memberAccess = {}, superAdmin = false) {
  const itemsSurface = Boolean(modules.products || modules.system);
  const menuEnabled = isProjectCapabilityEnabled(project, "gastronomicMenu") && itemsSurface;
  const tablesEnabled = isProjectCapabilityEnabled(project, "tables");
  const reservationsEnabled = isProjectCapabilityEnabled(project, "tableReservations");
  const kitchenEnabled = isProjectCapabilityEnabled(project, "kitchenOrders") && modules.orders;
  const recipesEnabled = isProjectCapabilityEnabled(project, "recipes") && itemsSurface;
  const access = normalizeProjectAccess(memberAccess);

  // Mientras el acceso del miembro todavía se está resolviendo, conservar la
  // navegación visible por capacidad y dejar que el gate existente cierre la
  // autorización. Así evitamos parpadeos o falsos negativos al cargar el proyecto.
  if (superAdmin || !access.resolved) {
    return menuEnabled || tablesEnabled || reservationsEnabled || kitchenEnabled || recipesEnabled;
  }

  const menu = menuEnabled && hasProjectPermission(access, "items.read");
  const tables = tablesEnabled && hasProjectPermission(access, "tables.read");
  const reservations = reservationsEnabled
    && hasProjectRole(access, ["owner", "admin", "vendedor", "viewer", "facturacion"]);
  const kitchen = kitchenEnabled
    && hasProjectRole(access, ["owner", "admin", "administrativo", "vendedor", "cajero", "facturacion", "viewer"]);
  const recipes = recipesEnabled && hasProjectPermission(access, "items.read");
  return menu || tables || reservations || kitchen || recipes;
}

function projectUsesServiceAgenda(project, modules = {}) {
  if (!modules.products) return false;

  const profile = normalizeProjectBusinessProfile(project?.businessProfile);
  // Los proyectos históricos no tenían perfil ni objetivos: conservar Agenda evita
  // romper una superficie que antes estaba disponible por el módulo products.
  if (!profile) return true;

  const goals = new Set(projectBusinessGoals(project));
  if (["services", "gym"].includes(profile)) {
    // En servicios/gimnasios la agenda es parte natural de la operación. Si el
    // onboarding guardó objetivos, cualquiera de estos dos confirma además la intención.
    return !goals.size || goals.has("offerServices") || goals.has("takeReservations");
  }

  // Comercio y restaurante no reciben una agenda de servicios sólo por tener catálogo.
  // En restaurantes las reservas de mesa se habilitan por capacidad y van a /mesas.
  return false;
}

function tableOperationLabel(project) {
  const tablesEnabled = isProjectCapabilityEnabled(project, "tables");
  const reservationsEnabled = isProjectCapabilityEnabled(project, "tableReservations");
  if (tablesEnabled && reservationsEnabled) return "Mesas y reservas";
  if (reservationsEnabled) return "Reservas de mesa";
  return "Mesas";
}

function tableOperationMobileLabel(project) {
  const tablesEnabled = isProjectCapabilityEnabled(project, "tables");
  const reservationsEnabled = isProjectCapabilityEnabled(project, "tableReservations");
  if (reservationsEnabled && !tablesEnabled) return "Reservas";
  return "Mesas";
}

function tableOperationHelper(project) {
  const tablesEnabled = isProjectCapabilityEnabled(project, "tables");
  const reservationsEnabled = isProjectCapabilityEnabled(project, "tableReservations");
  if (tablesEnabled && reservationsEnabled) return "Plano, operación del salón y reservas de mesa";
  if (reservationsEnabled) return "Reservas de mesa separadas de los turnos de servicios";
  return "Plano y operación de mesas";
}

export const PROJECT_PAGE_TOOLS = Object.freeze([
  {
    key: "page-editor",
    label: "Editar página",
    mobileLabel: "Editor",
    shortLabel: "Editor",
    iconKey: "page",
    billingSensitive: false,
    billingSuspensionSensitive: true,
    permissions: Object.freeze(["project.view", "project.settings.manage"]),
    permissionMode: "all",
  },
  {
    key: "page-publication",
    label: "Publicación y dominio",
    mobileLabel: "Publicación",
    shortLabel: "Publicación",
    iconKey: "settings",
    billingSensitive: false,
    billingSuspensionSensitive: true,
    permissions: Object.freeze(["project.view", "project.settings.manage"]),
    permissionMode: "all",
    matchSearchParams: Object.freeze({ area: "domain" }),
  },
]);

const PROJECT_NAVIGATION_TOOL_DEFINITIONS = Object.freeze([
  {
    key: "home",
    areaKey: DASHBOARD_AREA_KEYS.HOME,
    label: "Inicio",
    mobileLabel: "Inicio",
    iconKey: "home",
    starterAreas: Object.freeze([]),
    billingSensitive: false,
    permissions: Object.freeze(["project.view"]),
    visible: ({ hasProject }) => hasProject,
    path: ({ projectBase }) => `${projectBase}/inicio`,
  },
  {
    key: "landing",
    areaKey: DASHBOARD_AREA_KEYS.PAGE,
    label: "Diseñá tu Web",
    mobileLabel: "Mi web",
    iconKey: "page",
    starterAreas: Object.freeze(["landing"]),
    billingSensitive: false,
    billingSuspensionSensitive: true,
    permissions: Object.freeze(["project.view", "project.settings.manage"]),
    permissionMode: "all",
    visible: ({ hasProject, modules }) => hasProject && modules.landing,
    path: ({ projectBase }) => `${projectBase}/pagina`,
    secondaryKind: "page",
    // La entrada principal de Página es su hub (/pagina). Publicación y dominio
    // sigue siendo una herramienta secundaria de Configuración y no debe
    // secuestrar el click del área Página.
    areaRestoreMemory: false,
    restoreMemory: false,
  },
  {
    key: "products",
    areaKey: DASHBOARD_AREA_KEYS.SALES,
    label: ({ modules }) => modules.system ? "Catálogo e ítems" : "Productos y servicios",
    mobileLabel: "Catálogo",
    helper: ({ modules }) => modules.system
      ? "Productos, servicios, categorías e ítems operativos"
      : "Productos, servicios y categorías del catálogo",
    iconKey: "products",
    starterAreas: Object.freeze(["items"]),
    billingSensitive: true,
    permissions: ({ search }) => dashboardSearchParam(search, "crear") === "1"
      ? ["items.read", "items.create"]
      : ["items.read"],
    permissionMode: "all",
    visible: ({ hasProject, modules }) => hasProject && (modules.products || modules.system),
    path: ({ project, projectBase, modules }) => dashboardProjectItemsPath(project, { projectBase, modules }),
    active: ({ projectRelativePath, search, project, modules }) => {
      const view = dashboardSearchParam(search, "vista").toLowerCase();
      const itemPath = dashboardProjectItemsPath(project, { projectBase: "", modules })
        .replace(/^\/dashboard\/proyectos\/[^/]+/, "");
      return normalizeDashboardPathname(projectRelativePath || "/") === itemPath
        && !["carta", "recetas"].includes(view);
    },
  },
  {
    key: "agenda",
    areaKey: DASHBOARD_AREA_KEYS.OPERATION,
    label: "Agenda de servicios",
    mobileLabel: "Turnos",
    helper: "Disponibilidad, turnos y reservas de servicios",
    iconKey: "agenda",
    starterAreas: Object.freeze(["agenda"]),
    billingSensitive: true,
    // La pantalla carga disponibilidad y bloqueos además del listado de turnos;
    // facturación puede listar appointments en backend, pero no esas dos piezas.
    roles: ({ search }) => dashboardSearchParam(search, "nuevo") === "1"
      ? ["owner", "admin", "vendedor"]
      : ["owner", "admin", "vendedor", "viewer"],
    visible: ({ hasProject, project, modules }) => hasProject && projectUsesServiceAgenda(project, modules),
    path: ({ projectBase }) => `${projectBase}/agenda`,
  },
  {
    key: "gastronomy",
    areaKey: DASHBOARD_AREA_KEYS.GASTRONOMY,
    label: "Gastronomía",
    mobileLabel: "Gastronomía",
    helper: "Carta y operación gastronómica según las capacidades activas",
    iconKey: "restaurant",
    starterAreas: Object.freeze(["items", "agenda", "orders", "system"]),
    progressiveStarterAreas: Object.freeze([]),
    billingSensitive: true,
    access: ({ project, modules, memberAccess, superAdmin }) => projectHasAccessibleGastronomy(project, modules, memberAccess, superAdmin),
    visible: ({ hasProject, project, modules, memberAccess, superAdmin }) => (
      hasProject && projectHasAccessibleGastronomy(project, modules, memberAccess, superAdmin)
    ),
    path: ({ projectBase }) => `${projectBase}/mesas`,
    active: ({ projectRelativePath, search, project, modules }) => {
      const relative = normalizeDashboardPathname(projectRelativePath || "/");
      const view = dashboardSearchParam(search, "vista").toLowerCase();
      const itemsPath = dashboardProjectItemsPath(project, { projectBase: "", modules })
        .replace(/^\/dashboard\/proyectos\/[^/]+/, "");
      return (
        relative === "/mesas"
        || (relative === "/pedidos" && view === "comandas")
        || (relative === itemsPath && ["carta", "recetas"].includes(view))
      );
    },
    secondaryKind: "gastronomy",
  },
  {
    key: "orders",
    areaKey: DASHBOARD_AREA_KEYS.SALES,
    label: "Pedidos",
    mobileLabel: "Pedidos",
    iconKey: "orders",
    starterAreas: Object.freeze(["orders"]),
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin", "vendedor", "deposito", "facturacion", "viewer"]),
    visible: ({ hasProject, modules }) => hasProject && modules.orders,
    path: ({ projectBase }) => `${projectBase}/pedidos`,
    active: ({ projectRelativePath, search }) => (
      normalizeDashboardPathname(projectRelativePath || "/") === "/pedidos"
      && dashboardSearchParam(search, "vista").toLowerCase() !== "comandas"
    ),
  },
  {
    key: "registered-sales",
    areaKey: DASHBOARD_AREA_KEYS.SALES,
    label: "Ventas registradas",
    mobileLabel: "Ventas",
    helper: "Ventas cargadas en Gestión del negocio",
    iconKey: "management-movements",
    starterAreas: Object.freeze(["system"]),
    billingSensitive: true,
    permissions: Object.freeze(["erp.movements.read"]),
    access: ({ search, memberAccess }) => dashboardSearchParam(search, "nuevo") !== "1"
      || hasAnyProjectPermission(memberAccess, ["erp.sales.create", "erp.movements.create"]),
    visible: ({ hasProject, modules }) => hasProject && modules.system,
    path: ({ projectBase }) => `${projectBase}/sistema/movimientos?vista=ventas`,
    active: ({ projectRelativePath, search }) => isProjectSalesMovementRoute(projectRelativePath, search),
    restoreMemory: false,
  },
  {
    key: "ecommerce",
    areaKey: DASHBOARD_AREA_KEYS.SALES,
    label: "Cobros online",
    mobileLabel: "Cobros",
    helper: "Checkout, medios de pago online y moneda de las operaciones web",
    iconKey: "payments",
    starterAreas: Object.freeze(["orders"]),
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin"]),
    visible: ({ hasProject, modules }) => hasProject && modules.ecommerce,
    path: ({ projectBase }) => `${projectBase}/configuracion/ecommerce`,
    // Aunque la URL legacy conserve /configuracion, Cobros online pertenece a Ventas.
    // Esto evita que la segunda barra cambie de área al abrir este botón.
    active: ({ projectRelativePath }) => (
      resolveProjectSettingsSectionFromRoute(projectRelativePath) === "ecommerce"
    ),
    restoreMemory: false,
  },
  {
    key: "customers",
    areaKey: DASHBOARD_AREA_KEYS.CUSTOMERS,
    label: "CRM de clientes",
    mobileLabel: "Clientes",
    iconKey: "customers",
    starterAreas: Object.freeze(["customers"]),
    billingSensitive: true,
    permissions: Object.freeze(["customers.read"]),
    visible: ({ hasProject }) => hasProject,
    path: ({ projectBase }) => `${projectBase}/clientes`,
    secondaryKind: "customers",
    areaEntryKey: "customers-crm",
  },
  {
    key: "real-estate",
    areaKey: DASHBOARD_AREA_KEYS.OPERATION,
    label: "Inmobiliaria",
    mobileLabel: "Inmobiliaria",
    helper: "Propiedades, contratos y cobranzas de alquileres",
    iconKey: "management",
    starterAreas: Object.freeze(["landing", "customers", "system"]),
    billingSensitive: true,
    permissions: ({ project, modules }) => [
      modules.properties && isProjectCapabilityEnabled(project, "properties") ? "properties.read" : "",
      modules.leases && isProjectCapabilityEnabled(project, "leases") ? "leases.read" : "",
      modules.lease_collections && isProjectCapabilityEnabled(project, "leaseCollections") ? "lease_collections.read" : "",
    ].filter(Boolean),
    visible: ({ hasProject, project, modules }) => hasProject && Boolean(
      (modules.properties && isProjectCapabilityEnabled(project, "properties"))
      || (modules.leases && isProjectCapabilityEnabled(project, "leases"))
      || (modules.lease_collections && isProjectCapabilityEnabled(project, "leaseCollections"))
    ),
    path: ({ projectBase }) => `${projectBase}/inmobiliaria`,
    active: ({ projectRelativePath }) => projectRelativePath.startsWith("/inmobiliaria"),
  },
  {
    key: "system",
    areaKey: DASHBOARD_AREA_KEYS.MANAGEMENT,
    label: "Gestión del negocio",
    mobileLabel: "Gestión",
    iconKey: "management",
    starterAreas: Object.freeze(["system"]),
    progressiveStarterAreas: Object.freeze(["system", "items"]),
    billingSensitive: true,
    permissions: Object.freeze([
      "erp.overview.read",
      "erp.movements.read",
      "cash.registers.read",
      "cash.sessions.read",
      "cash.movements.read",
      "erp.stock.read",
      "erp.ledger.read",
      "erp.transactions.read",
      "erp.tax_profiles.read",
    ]),
    visible: ({ hasProject, modules, superAdmin }) => hasProject && (superAdmin || modules.system),
    path: ({ projectBase }) => `${projectBase}/sistema`,
    active: ({ projectRelativePath, search }) => (
      projectRelativePath.startsWith("/sistema") &&
      !projectRelativePath.startsWith("/sistema/items") &&
      !isProjectSalesMovementRoute(projectRelativePath, search)
    ),
    secondaryKind: "management",
  },
  {
    key: "stats",
    areaKey: DASHBOARD_AREA_KEYS.STATS,
    label: "Estadísticas",
    mobileLabel: "Estadísticas",
    iconKey: "stats",
    starterAreas: Object.freeze(["stats"]),
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin", "vendedor", "deposito", "facturacion", "viewer"]),
    visible: ({ hasProject, modules }) => hasProject && modules.stats,
    path: ({ projectBase }) => `${projectBase}/estadisticas`,
  },
  {
    key: "settings",
    areaKey: DASHBOARD_AREA_KEYS.SETTINGS,
    label: "Configuración del proyecto",
    mobileLabel: "Configuración",
    iconKey: "settings",
    starterAreas: Object.freeze([]),
    billingSensitive: false,
    visible: ({ hasProject }) => hasProject,
    path: ({ projectBase }) => `${projectBase}/configuracion`,
    active: ({ projectRelativePath }) => {
      const section = resolveProjectSettingsSectionFromRoute(projectRelativePath);
      return Boolean(
        normalizeDashboardPathname(projectRelativePath || "/") === "/configuracion" ||
        (section && isProjectSettingsNavigationSection(section))
      );
    },
    secondaryKind: "settings",
  },
  {
    key: "activity",
    areaKey: DASHBOARD_AREA_KEYS.MORE,
    label: "Actividad",
    mobileLabel: "Actividad",
    helper: "Historial y recorridos del proyecto",
    iconKey: "activity",
    starterAreas: Object.freeze([]),
    billingSensitive: false,
    roles: Object.freeze(["owner", "admin", "vendedor", "deposito", "facturacion", "viewer"]),
    visible: ({ hasProject }) => hasProject,
    path: ({ projectBase }) => `${projectBase}/actividad`,
  },
  {
    key: "notifications",
    areaKey: DASHBOARD_AREA_KEYS.MORE,
    label: "Notificaciones",
    mobileLabel: "Notificaciones",
    helper: "Alertas internas y campañas web push",
    iconKey: "notifications",
    starterAreas: Object.freeze([]),
    billingSensitive: true,
    roles: Object.freeze(["owner", "admin", "vendedor", "deposito", "facturacion", "viewer"]),
    visible: ({ hasProject }) => hasProject,
    path: ({ projectBase }) => `${projectBase}/configuracion/notificaciones`,
    active: ({ projectRelativePath }) => (
      resolveProjectSettingsSectionFromRoute(projectRelativePath) === "notificaciones"
    ),
  },
]);

const GLOBAL_NAVIGATION_TOOL_DEFINITIONS = Object.freeze([
  {
    key: "home",
    areaKey: DASHBOARD_AREA_KEYS.HOME,
    label: "Inicio",
    iconKey: "home",
    path: "/dashboard",
    exact: true,
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.WORKSPACE,
  },
  {
    key: "projects",
    areaKey: DASHBOARD_AREA_KEYS.ACCOUNT,
    label: "Mis proyectos",
    superAdminLabel: "Clientes / proyectos",
    iconKey: "projects",
    path: "/dashboard/proyectos",
    exact: true,
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ACCOUNT,
  },
  {
    key: "referrals",
    areaKey: DASHBOARD_AREA_KEYS.ACCOUNT,
    label: "Red de referidos",
    iconKey: "referrals",
    path: "/dashboard/referidos",
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ACCOUNT,
  },
  {
    key: "security",
    areaKey: DASHBOARD_AREA_KEYS.ACCOUNT,
    label: "Seguridad",
    iconKey: "security",
    path: "/dashboard/seguridad",
    surface: "account",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ACCOUNT,
  },
  {
    key: "owner-finance",
    areaKey: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Mis Finanzas",
    iconKey: "management-accounting",
    path: "/dashboard/owner/finanzas",
    platformOwnerOnly: true,
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  },
  {
    key: "superpanel",
    areaKey: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Superpanel",
    iconKey: "superpanel",
    path: "/dashboard/superpanel",
    superAdminOnly: true,
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  },
  {
    key: "global-activity",
    areaKey: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Actividad global",
    iconKey: "activity",
    path: "/dashboard/actividad",
    superAdminOnly: true,
    surface: "secondary",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  },
  {
    key: "analytics-admin",
    areaKey: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Analítica",
    iconKey: "stats",
    path: "/dashboard/analitica",
    superAdminOnly: true,
    surface: "secondary",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  },
  {
    key: "users",
    areaKey: DASHBOARD_AREA_KEYS.ADMINISTRATION,
    label: "Usuarios",
    iconKey: "users",
    path: "/dashboard/usuarios",
    superAdminOnly: true,
    surface: "main",
    groupKey: DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  },
]);

function resolveValue(value, context) {
  return typeof value === "function" ? value(context) : value;
}

export function dashboardProjectKey(project) {
  return String(project?.slug || project?.id || project?._id || "").trim();
}

export function dashboardProjectBase(project) {
  const key = dashboardProjectKey(project);
  return key ? `/dashboard/proyectos/${key}` : "/dashboard/proyectos";
}

/**
 * Resuelve una sola ruta canónica para administrar el catálogo/los ítems.
 * Los proyectos con Gestión del negocio usan la superficie integrada del ERP;
 * el catálogo simple conserva /productos. Las rutas legacy siguen existiendo,
 * pero los accesos del panel deben converger acá para no duplicar la entidad.
 */
export function dashboardProjectItemsPath(project, { projectBase = "", modules = null } = {}) {
  const base = projectBase || dashboardProjectBase(project);
  const resolvedModules = normalizeProjectModules(modules || project?.modules || {});
  return resolvedModules.system ? `${base}/sistema/items` : `${base}/productos`;
}

export function normalizeDashboardPathname(pathname = "") {
  let normalizedPath = String(pathname || "").trim();

  // Los consumidores normales pasan useLocation().pathname, pero mantener este
  // normalizador acá evita que un deep-link con query/hash o una barra final
  // produzca un estado activo distinto al de la misma ruta navegada internamente.
  const queryIndex = normalizedPath.indexOf("?");
  const hashIndex = normalizedPath.indexOf("#");
  const suffixIndexes = [queryIndex, hashIndex].filter((index) => index >= 0);
  if (suffixIndexes.length) {
    normalizedPath = normalizedPath.slice(0, Math.min(...suffixIndexes));
  }

  if (!normalizedPath) return "/";
  if (normalizedPath.length > 1) normalizedPath = normalizedPath.replace(/\/+$/, "");
  return normalizedPath || "/";
}

export function isDashboardRouteActive(pathname, path, exact = false) {
  if (!path) return false;
  const currentPath = normalizeDashboardPathname(pathname);
  const candidatePath = normalizeDashboardPathname(path);
  if (exact) return currentPath === candidatePath;
  return currentPath === candidatePath || currentPath.startsWith(`${candidatePath}/`);
}

export function dashboardProjectRelativePath(pathname = "") {
  const normalizedPath = normalizeDashboardPathname(pathname);
  const match = normalizedPath.match(/^\/dashboard\/proyectos\/[^/]+(\/.*)?$/);
  return match ? (match[1] || "") : "";
}

export function resolveProjectSettingsSectionFromRoute(projectRelativePath = "") {
  const normalizedRelativePath = normalizeDashboardPathname(
    projectRelativePath || "/",
  );
  const relativePath = normalizedRelativePath === "/" ? "" : normalizedRelativePath;
  const canonicalMatch = relativePath.match(/^\/configuracion\/([^/]+)$/);
  const canonicalSection = canonicalMatch?.[1] || "";

  if (canonicalSection) {
    if (canonicalSection === "funcionalidades") return "suscripcion";
    const exists = PROJECT_SETTINGS_SECTION_DEFINITIONS.some(
      (section) => section.value === canonicalSection,
    );
    return exists ? canonicalSection : null;
  }

  return PROJECT_SETTINGS_ROUTE_SECTION_ALIASES[relativePath] || null;
}

function isProjectSettingsNavigationSection(sectionValue = "") {
  const section = PROJECT_SETTINGS_SECTION_DEFINITIONS.find(
    (candidate) => candidate.value === sectionValue,
  );
  return Boolean(section && section.hideFromSettingsNavigation !== true);
}

function normalizeDashboardSearch(search = "") {
  const raw = String(search || "").trim();
  if (!raw || raw === "?") return "";
  return raw.startsWith("?") ? raw : `?${raw}`;
}

function dashboardSearchParam(search = "", key = "") {
  if (!key) return "";
  try {
    return new URLSearchParams(normalizeDashboardSearch(search)).get(key) || "";
  } catch {
    return "";
  }
}

function isProjectWebPresenceRoute(projectRelativePath = "", search = "") {
  const relative = normalizeDashboardPathname(projectRelativePath || "/");
  return relative === "/configuracion/pagina" ||
    (relative === "/configuracion/general" && dashboardSearchParam(search, "area") === "domain");
}

function isProjectSalesMovementRoute(projectRelativePath = "", search = "") {
  const relative = normalizeDashboardPathname(projectRelativePath || "/");
  return relative === "/sistema/movimientos" && dashboardSearchParam(search, "vista") === "ventas";
}

function navigationToolSearchMatches(tool = {}, search = "") {
  // Un subdestino que ya resolvió `active` con el pathname/query actual debe
  // participar del gate aunque su enlace canónico agregue parámetros distintos.
  // Esto cubre acciones directas como ?accion=crear-mesa sin degradarlas al padre.
  if (tool.active) return true;
  const params = new URLSearchParams(normalizeDashboardSearch(search));
  const required = tool.matchSearchParams || {};
  for (const [key, value] of Object.entries(required)) {
    if (params.get(key) !== String(value)) return false;
  }
  return true;
}

export function dashboardProjectSwitchPath(
  pathname = "",
  project = null,
  { search = "", superAdmin = false } = {},
) {
  const currentPath = normalizeDashboardPathname(pathname || "/dashboard");
  const route = resolveDashboardRouteContext(currentPath);
  if (!route.isProject || !project) return currentPath;

  const projectBase = dashboardProjectBase(project);
  const relativePath = dashboardProjectRelativePath(currentPath);
  const candidatePath = `${projectBase}${relativePath}`;
  const navigation = resolveProjectNavigation({
    project,
    memberAccess: project?.memberAccess,
    superAdmin,
    pathname: candidatePath,
    search,
  });
  const candidateTool = findProjectNavigationToolByPath(navigation, candidatePath, search);

  // Al cambiar de proyecto preservamos la herramienta/contexto sólo cuando existe
  // también en el destino y el miembro puede verla. Evita caer en rutas ocultas por
  // perfil, capacidad, módulo, permiso o rol sin convertir el cambio de proyecto en
  // un reset global. Los bloqueos temporales de billing siguen siendo visibles y se
  // conservan para que el usuario vea el estado y su vía de recuperación.
  if (candidateTool && candidateTool.visible !== false) return candidatePath;

  // Deep links de compatibilidad (/billing, /ecommerce, /notificaciones y la raíz
  // histórica de proyecto) se canonizan a la sección equivalente si sigue visible.
  const settingsSection = resolveProjectSettingsSectionFromRoute(relativePath);
  if (settingsSection) {
    const settingsTool = navigation.toolsByKey.settings?.secondaryTools?.find(
      (tool) => tool.key === `settings-${settingsSection}` && tool.visible,
    );
    if (settingsTool?.path) return settingsTool.path;
  }

  return navigation.toolsByKey.home?.path || `${projectBase}/inicio`;
}

export function resolveDashboardRouteContext(pathname = "") {
  const normalizedPath = normalizeDashboardPathname(pathname);
  const match = normalizedPath.match(/^\/dashboard\/proyectos\/([^/]+)(?:\/.*)?$/);
  const encodedProjectKey = match?.[1] || "";
  let projectKey = encodedProjectKey;

  if (encodedProjectKey) {
    try {
      projectKey = decodeURIComponent(encodedProjectKey);
    } catch {
      projectKey = encodedProjectKey;
    }
  }

  const scope = projectKey
    ? DASHBOARD_NAVIGATION_SCOPES.PROJECT
    : DASHBOARD_NAVIGATION_SCOPES.GLOBAL;

  return {
    pathname: normalizedPath,
    scope,
    projectKey,
    isGlobal: scope === DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
    isProject: scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT,
    requiresProject: scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT,
  };
}

function resolveProjectToolAuthorization(definition, context) {
  if (context.superAdmin) {
    return { allowed: true, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.AVAILABLE };
  }

  if (!context.memberAccess.resolved) {
    // Compatibilidad durante una carga o despliegue mixto: si el backend todavía
    // no adjuntó el acceso efectivo, no ocultamos herramientas por inferencia.
    // Los endpoints continúan siendo la autoridad final hasta resolverlo.
    return { allowed: true, resolved: false, reason: DASHBOARD_TOOL_ACCESS_REASONS.AVAILABLE };
  }

  if (context.memberAccess.status !== "active") {
    return { allowed: false, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.PERMISSION };
  }

  if (definition.access && !Boolean(resolveValue(definition.access, context))) {
    return { allowed: false, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.PERMISSION };
  }

  const roles = resolveValue(definition.roles, context) || [];
  if (roles.length && !hasProjectRole(context.memberAccess, roles)) {
    return { allowed: false, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.ROLE };
  }

  const permissions = resolveValue(definition.permissions, context) || [];
  if (permissions.length) {
    const allowed = definition.permissionMode === "all"
      ? permissions.every((permission) =>
          hasProjectPermission(context.memberAccess, permission),
        )
      : hasAnyProjectPermission(context.memberAccess, permissions);
    if (!allowed) {
      return { allowed: false, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.PERMISSION };
    }
  }

  return { allowed: true, resolved: true, reason: DASHBOARD_TOOL_ACCESS_REASONS.AVAILABLE };
}

function resolveProjectToolState(definition, context, baseVisible) {
  if (!baseVisible) {
    return {
      state: DASHBOARD_TOOL_ACCESS_STATES.HIDDEN,
      reason: DASHBOARD_TOOL_ACCESS_REASONS.MODULE_OR_CAPABILITY,
      authorized: true,
      authorizationResolved: context.superAdmin || context.memberAccess.resolved,
      visible: false,
      available: false,
      locked: false,
    };
  }

  const authorization = resolveProjectToolAuthorization(definition, context);
  if (!authorization.allowed) {
    return {
      state: DASHBOARD_TOOL_ACCESS_STATES.HIDDEN,
      reason: authorization.reason,
      authorized: false,
      authorizationResolved: authorization.resolved,
      visible: false,
      available: false,
      locked: false,
    };
  }

  const lockedByBilling = Boolean(
    !context.superAdmin && (
      (definition.billingSensitive && context.billingAccess.isLandingOnly)
      || ((definition.billingSensitive || definition.billingSuspensionSensitive)
        && context.billingAccess.reason === "billing_suspended")
    ),
  );
  const lockedByProjectStatus = Boolean(
    definition.billingSensitive &&
      !context.superAdmin &&
      context.billingAccess.isSuspended
        && context.billingAccess.reason !== "billing_suspended",
  );
  const locked = lockedByBilling || lockedByProjectStatus;

  return {
    state: locked ? DASHBOARD_TOOL_ACCESS_STATES.LOCKED : DASHBOARD_TOOL_ACCESS_STATES.AVAILABLE,
    reason: lockedByBilling
      ? DASHBOARD_TOOL_ACCESS_REASONS.BILLING
      : lockedByProjectStatus
        ? DASHBOARD_TOOL_ACCESS_REASONS.PROJECT_STATUS
        : DASHBOARD_TOOL_ACCESS_REASONS.AVAILABLE,
    lockLabel: locked ? billingAccessLockLabel(context.billingAccess) : "",
    authorized: true,
    authorizationResolved: authorization.resolved,
    visible: true,
    available: !locked,
    locked,
  };
}

function resolveSecondaryTool(definition, context, {
  areaKey,
  parentKey,
  path,
  navigationDepth = 1,
  baseVisible = true,
} = {}) {
  const access = resolveProjectToolState(definition, context, Boolean(baseVisible));
  return {
    ...definition,
    scope: DASHBOARD_NAVIGATION_SCOPES.PROJECT,
    requiresProject: true,
    areaKey,
    parentKey,
    path,
    active: definition.active
      ? Boolean(definition.active(context))
      : isDashboardRouteActive(context.pathname, path, true),
    accessState: access.state,
    accessReason: access.reason,
    lockLabel: access.lockLabel || "",
    authorized: access.authorized,
    authorizationResolved: access.authorizationResolved,
    visible: access.visible,
    available: access.available,
    locked: access.locked,
    restricted: access.locked,
    navigationDepth,
  };
}

function resolveManagementSecondaryTools(context) {
  const systemEnabled = context.superAdmin || context.modules.system;

  return PROJECT_MANAGEMENT_PRIMARY_TOOLS.map((tool) => {
    const resolved = resolveSecondaryTool(tool, context, {
      areaKey: DASHBOARD_AREA_KEYS.MANAGEMENT,
      parentKey: "system",
      path: tool.path
        ? `${context.projectBase}/sistema/${tool.path}`
        : `${context.projectBase}/sistema`,
      navigationDepth: 1,
      baseVisible: context.hasProject && systemEnabled,
    });
    const secondaryTools = tool.key === "accounting"
      ? PROJECT_MANAGEMENT_ACCOUNTING_TOOLS.map((secondary) =>
          resolveSecondaryTool(secondary, context, {
            areaKey: DASHBOARD_AREA_KEYS.MANAGEMENT,
            parentKey: tool.key,
            path: `${context.projectBase}/sistema/${secondary.path}`,
            navigationDepth: 2,
            baseVisible: context.hasProject && systemEnabled,
          }),
        )
      : [];
    const contextualEntry = secondaryTools.find((secondary) =>
      secondary.visible && secondary.available,
    ) || secondaryTools.find((secondary) => secondary.visible) || null;

    return {
      ...resolved,
      // Un usuario que sólo puede leer Transacciones o Impuestos no debe entrar
      // primero a Libro contable y rebotar por permisos. El contenedor apunta a
      // su primera herramienta contable realmente accesible.
      path: tool.key === "accounting" && contextualEntry?.path
        ? contextualEntry.path
        : resolved.path,
      active: Boolean(
        resolved.active ||
        secondaryTools.some((secondary) => secondary.active),
      ),
      secondaryTools,
    };
  });
}

function resolvePageSecondaryTools(context) {
  return PROJECT_PAGE_TOOLS.map((tool) => {
    const projectKey = dashboardProjectKey(context.project);
    const path = tool.key === "page-publication"
      ? `${context.projectBase}/configuracion/pagina`
      : `/editor-web/${encodeURIComponent(projectKey)}`;

    return resolveSecondaryTool(
      {
        ...tool,
        matchSearchParams: tool.key === "page-publication" ? {} : tool.matchSearchParams,
        // El hub /pagina pertenece al área Página, pero no es el editor visual.
        // Ninguna opción secundaria debe fingir estar activa mientras el usuario
        // está en ese hub: "Editar página" entra al editor oficial y
        // "Publicación y dominio" vive en Configuración.
        active: () => false,
      },
      context,
      {
        areaKey: DASHBOARD_AREA_KEYS.PAGE,
        parentKey: "landing",
        path,
        navigationDepth: 1,
        baseVisible: context.hasProject && context.modules.landing,
      },
    );
  });
}

function resolveCustomerSecondaryTools(context) {
  return PROJECT_CUSTOMER_TOOLS.map((tool) => {
    const view = tool.key === "customers-pricing"
      ? "precios"
      : tool.key === "customers-memberships"
        ? "membresias"
        : tool.key === "customers-expirations"
          ? "vencimientos"
          : "crm";
    const path = view === "crm"
      ? `${context.projectBase}/clientes`
      : `${context.projectBase}/clientes?vista=${view}`;
    const capabilityVisible = !tool.capability
      || isProjectCapabilityEnabled(context.project, tool.capability);
    const membershipRecoveryVisible = tool.key === "customers-memberships"
      && !capabilityVisible
      && hasProjectPermission(context.memberAccess, "project.settings.manage");

    return resolveSecondaryTool(
      {
        ...tool,
        active: ({ projectRelativePath, search }) => {
          if (normalizeDashboardPathname(projectRelativePath || "/") !== "/clientes") return false;
          const requestedView = dashboardSearchParam(search, "vista").toLowerCase();
          const action = dashboardSearchParam(search, "accion").toLowerCase();
          if (tool.key === "customers-expirations") {
            return requestedView === "vencimientos" || action === "vencimientos";
          }
          if (tool.key === "customers-memberships") {
            return requestedView === "membresias" || ["asignar-plan", "renovar"].includes(action);
          }
          if (tool.key === "customers-pricing") return requestedView === "precios";
          if (action === "crear-cliente") return true;
          return (!requestedView || requestedView === "crm")
            && !["asignar-plan", "renovar", "vencimientos"].includes(action);
        },
      },
      context,
      {
        areaKey: DASHBOARD_AREA_KEYS.CUSTOMERS,
        parentKey: "customers",
        path,
        navigationDepth: 1,
        baseVisible: context.hasProject && (capabilityVisible || membershipRecoveryVisible),
      },
    );
  });
}

function resolveGastronomySecondaryTools(context) {
  const itemsPath = dashboardProjectItemsPath(context.project, {
    projectBase: context.projectBase,
    modules: context.modules,
  });
  const itemsSurface = Boolean(context.modules.products || context.modules.system);

  return PROJECT_GASTRONOMY_TOOLS.map((tool) => {
    const capabilityVisible = isProjectCapabilityEnabled(context.project, tool.capability);
    const dependencyVisible = tool.requiresModule
      ? Boolean(context.modules[tool.requiresModule])
      : tool.requiresItemsSurface
        ? itemsSurface
        : true;
    const path = tool.key === "gastronomy-menu"
      ? `${itemsPath}?vista=carta`
      : tool.key === "gastronomy-tables"
        ? `${context.projectBase}/mesas?vista=mesas`
        : tool.key === "gastronomy-reservations"
          ? `${context.projectBase}/mesas?vista=reservas`
          : tool.key === "gastronomy-kitchen"
            ? `${context.projectBase}/pedidos?vista=comandas`
            : `${itemsPath}?vista=recetas`;

    return resolveSecondaryTool(
      {
        ...tool,
        matchSearchParams: tool.key === "gastronomy-menu"
          ? { vista: "carta" }
          : tool.key === "gastronomy-tables"
            ? { vista: "mesas" }
            : tool.key === "gastronomy-reservations"
              ? { vista: "reservas" }
              : tool.key === "gastronomy-kitchen"
                ? { vista: "comandas" }
                : { vista: "recetas" },
        active: ({ projectRelativePath, search, project, modules }) => {
          const relative = normalizeDashboardPathname(projectRelativePath || "/");
          const view = dashboardSearchParam(search, "vista").toLowerCase();
          const relativeItems = dashboardProjectItemsPath(project, { projectBase: "", modules })
            .replace(/^\/dashboard\/proyectos\/[^/]+/, "");
          if (tool.key === "gastronomy-menu") return relative === relativeItems && view === "carta";
          if (tool.key === "gastronomy-tables") return relative === "/mesas" && (!view || view === "mesas" || view === "plano");
          if (tool.key === "gastronomy-reservations") return relative === "/mesas" && view === "reservas";
          if (tool.key === "gastronomy-kitchen") return relative === "/pedidos" && view === "comandas";
          return relative === relativeItems && view === "recetas";
        },
      },
      context,
      {
        areaKey: DASHBOARD_AREA_KEYS.GASTRONOMY,
        parentKey: "gastronomy",
        path,
        navigationDepth: 1,
        baseVisible: context.hasProject && capabilityVisible && dependencyVisible,
      },
    );
  });
}

function hasExistingProjectWebPresence(project) {
  const deployment = project?.deployment || {};
  const deploymentStatus = String(deployment.status || "").trim().toLowerCase();
  const customDomains = Array.isArray(deployment.customDomains) ? deployment.customDomains : [];
  const projectDomains = Array.isArray(project?.domains) ? project.domains : [];
  return Boolean(
    String(deployment.url || "").trim()
    || String(deployment.managedUrl || "").trim()
    || customDomains.some((entry) => String(entry?.name || "").trim())
    || projectDomains.some((entry) => String(entry || "").trim())
    || ["live", "failed", "provisioning", "not_configured"].includes(deploymentStatus)
  );
}

function resolveSettingsSecondaryTools(context) {
  return PROJECT_SETTINGS_SECTION_DEFINITIONS
    .filter((section) => section.hideFromSettingsNavigation !== true)
    .map((section) =>
      resolveSecondaryTool(
        {
          ...section,
          key: `settings-${section.value}`,
          active: ({ projectRelativePath, search }) => (
            resolveProjectSettingsSectionFromRoute(projectRelativePath) === section.value &&
            !(section.value === "general" && isProjectWebPresenceRoute(projectRelativePath, search))
          ),
        },
        context,
        {
          areaKey: DASHBOARD_AREA_KEYS.SETTINGS,
          parentKey: "settings",
          path: `${context.projectBase}/configuracion/${section.value}`,
          navigationDepth: 1,
          baseVisible: context.hasProject && (
            !section.requiresModule
            || Boolean(context.modules[section.requiresModule])
            || (section.requiresModule === "landing" && hasExistingProjectWebPresence(context.project))
          ),
        },
      ),
    );
}

function resolveProjectTool(definition, context) {
  const initialPath = resolveValue(definition.path, context);
  const relativePath = initialPath.startsWith(context.projectBase)
    ? initialPath.slice(context.projectBase.length)
    : "";
  const baseVisible = Boolean(resolveValue(definition.visible, context));
  const access = resolveProjectToolState(definition, context, baseVisible);
  const starterAreas = [...(definition.starterAreas || [])];
  let secondaryTools = [];

  if (definition.secondaryKind === "management") {
    secondaryTools = resolveManagementSecondaryTools(context);
  } else if (definition.secondaryKind === "settings") {
    secondaryTools = resolveSettingsSecondaryTools(context);
  } else if (definition.secondaryKind === "page") {
    secondaryTools = resolvePageSecondaryTools(context);
  } else if (definition.secondaryKind === "customers") {
    secondaryTools = resolveCustomerSecondaryTools(context);
  } else if (definition.secondaryKind === "gastronomy") {
    secondaryTools = resolveGastronomySecondaryTools(context);
  }

  let path = initialPath;
  if (definition.key === "system" && access.visible) {
    const defaultTool = secondaryTools.find((tool) => tool.key === "overview" && tool.visible)
      || secondaryTools.find((tool) => tool.visible);
    if (defaultTool?.path) path = defaultTool.path;
  }
  if (definition.key === "gastronomy" && access.visible) {
    const defaultTool = secondaryTools.find((tool) => tool.visible && tool.available)
      || secondaryTools.find((tool) => tool.visible);
    if (defaultTool?.path) path = defaultTool.path;
  }

  const ownActive = definition.active
    ? Boolean(definition.active({ ...context, path, relativePath }))
    : isDashboardRouteActive(context.projectRelativePath, relativePath);
  const active = Boolean(ownActive || secondaryTools.some((tool) => tool.active));
  const areaEntryTool = definition.areaEntryKey
    ? secondaryTools.find((tool) => tool.key === definition.areaEntryKey && tool.visible) || null
    : null;

  return {
    key: definition.key,
    scope: DASHBOARD_NAVIGATION_SCOPES.PROJECT,
    requiresProject: true,
    areaKey: definition.areaKey,
    label: resolveValue(definition.label, context),
    mobileLabel: resolveValue(definition.mobileLabel, context),
    helper: resolveValue(definition.helper, context),
    iconKey: definition.iconKey,
    path,
    active,
    accessState: access.state,
    accessReason: access.reason,
    lockLabel: access.lockLabel || "",
    authorized: access.authorized,
    authorizationResolved: access.authorizationResolved,
    visible: access.visible,
    available: access.available,
    locked: access.locked,
    restricted: access.locked,
    starterAreas,
    recommended: starterAreas.some((area) => context.starterAreaSet.has(area)),
    navigationDepth: 0,
    areaEntryPath: areaEntryTool?.path || "",
    areaRestoreMemory: definition.areaRestoreMemory !== false,
    restoreMemory: definition.restoreMemory !== false,
    secondaryTools,
  };
}

export function resolveProjectNavigation({
  project = null,
  memberAccess = undefined,
  superAdmin = false,
  pathname = "",
  search = "",
} = {}) {
  const modules = normalizeProjectModules(project?.modules);
  const billingAccess = getProjectBillingAccess(project);
  const projectBase = dashboardProjectBase(project);
  const starterAreas = projectStarterAreas(project);
  const normalizedMemberAccess = normalizeProjectAccess(
    memberAccess === undefined ? project?.memberAccess : memberAccess,
  );
  const context = {
    project,
    hasProject: Boolean(project),
    superAdmin: Boolean(superAdmin),
    pathname: String(pathname || ""),
    search: normalizeDashboardSearch(search),
    modules,
    billingAccess,
    memberAccess: normalizedMemberAccess,
    projectBase,
    projectRelativePath: dashboardProjectRelativePath(pathname),
    starterAreas,
    starterAreaSet: new Set(starterAreas),
  };

  const allTools = PROJECT_NAVIGATION_TOOL_DEFINITIONS.map((definition) =>
    resolveProjectTool(definition, context),
  );
  const tools = allTools.filter((tool) => tool.visible);
  const toolsByKey = Object.fromEntries(allTools.map((tool) => [tool.key, tool]));
  const areas = DASHBOARD_AREAS.map((area) => ({
    ...area,
    tools: tools.filter((tool) => tool.areaKey === area.key),
  })).filter((area) => area.tools.length > 0);

  return {
    scope: DASHBOARD_NAVIGATION_SCOPES.PROJECT,
    requiresProject: true,
    hasProject: Boolean(project),
    projectKey: dashboardProjectKey(project),
    projectBase,
    modules,
    billingAccess,
    memberAccess: normalizedMemberAccess,
    operationsRestricted: !superAdmin && (billingAccess.isLandingOnly || billingAccess.isSuspended),
    starterAreas,
    allTools,
    tools,
    toolsByKey,
    areas,
  };
}

export function flattenProjectNavigationTools(navigation) {
  const roots = Array.isArray(navigation?.allTools) ? navigation.allTools : [];
  const flattened = [];

  for (const tool of roots) {
    flattened.push(tool);
    for (const secondary of tool.secondaryTools || []) {
      flattened.push(secondary);
      for (const nested of secondary.secondaryTools || []) flattened.push(nested);
    }
  }

  return flattened;
}

function navigationToolPathMatches(tool = {}, pathname = "") {
  if (!tool.path) return false;
  if (isDashboardRouteActive(pathname, tool.path)) return true;

  const canonical = normalizeDashboardPathname(tool.path);
  const systemItemsMatch = canonical.match(/^(\/dashboard\/proyectos\/[^/]+)\/sistema\/items$/);
  if (systemItemsMatch && ["products", "gastronomy-menu", "gastronomy-recipes"].includes(tool.key)) {
    return isDashboardRouteActive(pathname, `${systemItemsMatch[1]}/productos`);
  }
  return false;
}

export function findProjectNavigationToolByPath(navigation, pathname = "", search = "") {
  const rawPath = String(pathname || "");
  const queryIndex = rawPath.indexOf("?");
  const derivedSearch = !search && queryIndex >= 0 ? rawPath.slice(queryIndex) : search;
  const normalizedPath = normalizeDashboardPathname(rawPath);
  const normalizedSearch = normalizeDashboardSearch(derivedSearch);
  return flattenProjectNavigationTools(navigation)
    .filter((tool) => (
      navigationToolPathMatches(tool, normalizedPath) &&
      navigationToolSearchMatches(tool, normalizedSearch)
    ))
    .sort((left, right) => {
      const activeDiff = Number(Boolean(right.active)) - Number(Boolean(left.active));
      if (activeDiff) return activeDiff;
      const pathDiff = normalizeDashboardPathname(right.path || "/").length
        - normalizeDashboardPathname(left.path || "/").length;
      if (pathDiff) return pathDiff;
      const depthDiff = Number(right.navigationDepth || 0) - Number(left.navigationDepth || 0);
      if (depthDiff) return depthDiff;
      return String(right.path || "").length - String(left.path || "").length;
    })[0] || null;
}

function decorateToolBranchActivity(tools = []) {
  return tools.map((tool) => {
    const secondaryTools = decorateToolBranchActivity(tool.secondaryTools || []);
    const descendantActive = secondaryTools.some((secondary) => secondary.active);
    return {
      ...tool,
      active: Boolean(tool.active || descendantActive),
      secondaryTools,
    };
  });
}

function findActiveToolBranch(tools = []) {
  for (const tool of tools) {
    if (!tool.active) continue;
    const childBranch = findActiveToolBranch(tool.secondaryTools || []);
    return childBranch.length ? [tool, ...childBranch] : [tool];
  }
  return [];
}

function resolveActiveProjectNavigation(projectNavigation, route) {
  if (!route?.isProject) {
    return {
      projectTool: null,
      secondaryTool: null,
      leafTool: null,
      branch: [],
    };
  }

  // La herramienta primaria define el área conceptual. Esto es importante en
  // rutas compartidas como /sistema/items: la página de ítems pertenece a
  // Ventas aunque también exista dentro de la estructura interna de Gestión.
  const projectTool = (projectNavigation?.tools || []).find((tool) => tool.active) || null;
  if (!projectTool) {
    return {
      projectTool: null,
      secondaryTool: null,
      leafTool: null,
      branch: [],
    };
  }

  const secondaryTree = decorateToolBranchActivity(projectTool.secondaryTools || []);
  const branch = findActiveToolBranch(secondaryTree);
  const secondaryTool = branch[0] || null;
  const leafTool = branch[branch.length - 1] || projectTool;

  return {
    projectTool,
    secondaryTool,
    leafTool,
    branch,
  };
}

export function resolveGlobalNavigation({ superAdmin = false, platformOwner = false, pathname = "" } = {}) {
  const tools = GLOBAL_NAVIGATION_TOOL_DEFINITIONS
    .filter((definition) => !definition.superAdminOnly || superAdmin)
    .filter((definition) => !definition.platformOwnerOnly || platformOwner)
    .map((definition) => ({
      ...definition,
      scope: DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
      requiresProject: false,
      label: superAdmin && definition.superAdminLabel
        ? definition.superAdminLabel
        : definition.label,
      active: definition.key === "home"
        ? (
            isDashboardRouteActive(pathname, definition.path, definition.exact === true) ||
            /^\/dashboard\/proyectos\/[^/]+\/inicio$/.test(normalizeDashboardPathname(pathname))
          )
        : isDashboardRouteActive(pathname, definition.path, definition.exact === true),
    }));

  const workspaceTools = tools.filter(
    (tool) => tool.groupKey === DASHBOARD_GLOBAL_GROUP_KEYS.WORKSPACE,
  );
  const administrationTools = tools.filter(
    (tool) => tool.groupKey === DASHBOARD_GLOBAL_GROUP_KEYS.ADMINISTRATION,
  );
  const accountTools = tools.filter(
    (tool) => tool.groupKey === DASHBOARD_GLOBAL_GROUP_KEYS.ACCOUNT,
  );

  return {
    scope: DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
    requiresProject: false,
    accountContext: DASHBOARD_ACCOUNT_CONTEXT,
    tools,
    workspaceTools,
    administrationTools,
    accountTools,
    activeTool: tools.find((tool) => tool.active) || null,
  };
}

export function resolveDashboardNavigation({
  project = null,
  memberAccess = undefined,
  superAdmin = false,
  platformOwner = false,
  pathname = "",
  search = "",
} = {}) {
  const normalizedPathname = normalizeDashboardPathname(pathname);
  const route = resolveDashboardRouteContext(normalizedPathname);
  const global = resolveGlobalNavigation({ superAdmin, platformOwner, pathname: normalizedPathname });
  const projectNavigation = resolveProjectNavigation({
    project,
    memberAccess,
    superAdmin,
    pathname: normalizedPathname,
    search,
  });
  const projectActive = resolveActiveProjectNavigation(projectNavigation, route);
  const activeGlobalTool = route.isGlobal ? global.activeTool : null;
  const activeAreaKey = route.isProject
    ? projectActive.projectTool?.areaKey || null
    : activeGlobalTool?.areaKey || null;
  const activeArea = DASHBOARD_AREAS.find((area) => area.key === activeAreaKey) || null;
  const activeProjectArea = route.isProject
    ? projectNavigation.areas.find((area) => area.key === activeAreaKey) || null
    : null;
  const usesContainerSecondary = Boolean(
    activeProjectArea?.tools?.length === 1 &&
      projectActive.projectTool?.secondaryTools?.length,
  );
  const activeSecondaryTool = route.isProject
    ? (usesContainerSecondary
        ? projectActive.secondaryTool || projectActive.projectTool
        : projectActive.projectTool)
    : activeGlobalTool;

  return {
    scope: route.scope,
    route,
    global,
    project: projectNavigation,
    accountContext: global.accountContext,
    hasActiveProject: projectNavigation.hasProject,
    showProjectBillingContext: route.isProject && projectNavigation.hasProject,
    activeAreaKey,
    activeArea,
    activeGlobalTool,
    activeProjectTool: projectActive.projectTool,
    // Representa la opción que la navegación secundaria del área debe considerar
    // activa. En Gestión baja un nivel porque "system" es sólo el contenedor.
    activeSecondaryTool,
    activeLeafTool: route.isProject ? projectActive.leafTool : activeGlobalTool,
    activeToolBranch: route.isProject && projectActive.projectTool
      ? [projectActive.projectTool, ...projectActive.branch]
      : (activeGlobalTool ? [activeGlobalTool] : []),
    // Se conserva el arreglo histórico para consumidores existentes, pero su
    // origen sigue siendo exclusivamente la URL actual.
    activeProjectTools: route.isProject
      ? projectNavigation.tools.filter((tool) => tool.active)
      : [],
  };
}

export function resolveDashboardPrimaryAreas(navigation = {}) {
  const route = navigation?.route || {};
  const projectAreas = Array.isArray(navigation?.project?.areas)
    ? navigation.project.areas
    : [];
  const globalTools = Array.isArray(navigation?.global?.tools)
    ? navigation.global.tools
    : [];
  const activeAreaKey = navigation?.activeAreaKey || null;

  return DASHBOARD_AREAS.map((area) => {
    const projectArea = projectAreas.find((candidate) => candidate.key === area.key) || null;
    const projectTools = Array.isArray(projectArea?.tools) ? projectArea.tools : [];
    const activeProjectAreaTool = projectTools.find((tool) => tool.active) || null;
    const projectDestination = activeProjectAreaTool
      || projectTools.find((tool) => tool.available)
      || projectTools[0]
      || null;

    const matchingGlobalTools = globalTools.filter((tool) => tool.areaKey === area.key);
    const activeGlobalAreaTool = matchingGlobalTools.find((tool) => tool.active) || null;
    const globalDestination = activeGlobalAreaTool
      || matchingGlobalTools.find((tool) => tool.surface === "main")
      || matchingGlobalTools[0]
      || null;

    // La navegación principal no mezcla taxonomías. Dentro de un proyecto sólo
    // muestra áreas operativas del proyecto; en rutas globales muestra únicamente
    // Inicio/Cuenta y, para superadmin, Administración. Los accesos globales siguen
    // disponibles desde los controles globales de cuenta y administración.
    const destination = route.isProject ? projectDestination : globalDestination;
    const visible = Boolean(destination);

    return {
      ...area,
      visible,
      active: area.key === activeAreaKey,
      path: destination?.areaEntryPath || destination?.path || null,
      scope: destination?.scope || (route.isProject
        ? DASHBOARD_NAVIGATION_SCOPES.PROJECT
        : DASHBOARD_NAVIGATION_SCOPES.GLOBAL),
      locked: Boolean(destination?.locked || destination?.restricted),
      accessState: destination?.accessState || null,
      accessReason: destination?.accessReason || null,
      lockLabel: destination?.lockLabel || "",
      destinationKey: destination?.key || null,
      restoreMemory: destination?.areaRestoreMemory !== false,
    };
  }).filter((area) => area.visible && area.path);
}

export function resolveDashboardSecondaryNavigation(navigation = {}) {
  const route = navigation?.route || {};
  const activeAreaKey = navigation?.activeAreaKey || null;

  if (!activeAreaKey) {
    return {
      scope: route.scope || DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
      area: null,
      tools: [],
      context: null,
    };
  }

  const area = DASHBOARD_AREAS.find((candidate) => candidate.key === activeAreaKey) || null;
  if (!area) {
    return {
      scope: route.scope || DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
      area: null,
      tools: [],
      context: null,
    };
  }

  // Inicio es un área de resumen, no una colección de herramientas. Mantener la
  // secundaria vacía evita duplicar el mismo acceso debajo de la navegación principal.
  if (activeAreaKey === DASHBOARD_AREA_KEYS.HOME) {
    return {
      scope: route.scope || DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
      area,
      tools: [],
      context: null,
    };
  }

  if (route.isProject) {
    const projectArea = (navigation?.project?.areas || [])
      .find((candidate) => candidate.key === activeAreaKey) || null;
    const areaTools = Array.isArray(projectArea?.tools) ? projectArea.tools : [];

    // Cuando un área real está representada por una única herramienta contenedora
    // (hoy Gestión del negocio), exponemos su primer nivel ya resuelto. Así la barra
    // contextual no inventa rutas ni duplica reglas de permisos/capacidades.
    const singleContainer = areaTools.length === 1
      ? areaTools[0]
      : null;

    // Un área que ya representa una única pantalla (por ejemplo Estadísticas o
    // Agenda) no necesita repetir el mismo botón debajo de la navegación principal.
    // Los contenedores reales como Página, Clientes, Gastronomía, Gestión y
    // Configuración sí exponen acá sus herramientas secundarias.
    if (singleContainer && !(singleContainer.secondaryTools || []).length) {
      return {
        scope: DASHBOARD_NAVIGATION_SCOPES.PROJECT,
        area,
        tools: [],
        context: null,
      };
    }

    const resolvedTools = singleContainer?.secondaryTools?.length
      ? decorateToolBranchActivity(
          singleContainer.secondaryTools.filter((tool) =>
            tool.visible && tool.hideFromAreaNavigation !== true,
          ),
        )
      : areaTools;

    const visibleTools = resolvedTools.filter((tool) =>
      tool.visible !== false && tool.hideFromAreaNavigation !== true,
    );
    const contextualParent = visibleTools.find((tool) => (
      tool.active &&
      Array.isArray(tool.secondaryTools) &&
      tool.secondaryTools.some((secondary) => secondary.visible !== false)
    )) || null;
    const contextualTools = contextualParent
      ? decorateToolBranchActivity(
          contextualParent.secondaryTools.filter((tool) =>
            tool.visible !== false && tool.hideFromAreaNavigation !== true,
          ),
        )
      : [];

    return {
      scope: DASHBOARD_NAVIGATION_SCOPES.PROJECT,
      area,
      tools: visibleTools,
      context: contextualParent && contextualTools.length
        ? {
            parent: contextualParent,
            tools: contextualTools,
          }
        : null,
    };
  }

  const globalTools = Array.isArray(navigation?.global?.tools)
    ? navigation.global.tools
    : [];

  return {
    scope: DASHBOARD_NAVIGATION_SCOPES.GLOBAL,
    area,
    // En rutas globales la barra contextual representa el área completa. Seguridad
    // mantiene además el atajo del avatar, pero también forma parte de Cuenta para
    // que una cuenta sin proyectos pueda recorrer todo el contexto sin depender de él.
    tools: globalTools.filter((tool) => tool.areaKey === activeAreaKey),
    context: null,
  };
}


const DASHBOARD_NAVIGATION_MEMORY_PREFIX = "webprofit:dashboard-navigation-context:v1";

function normalizeDashboardLocation(location = {}) {
  const rawSearch = String(location?.search || "");
  const rawHash = String(location?.hash || "");
  return {
    pathname: normalizeDashboardPathname(location?.pathname || "/dashboard"),
    search: rawSearch && rawSearch !== "?"
      ? (rawSearch.startsWith("?") ? rawSearch : `?${rawSearch}`)
      : "",
    hash: rawHash && rawHash !== "#"
      ? (rawHash.startsWith("#") ? rawHash : `#${rawHash}`)
      : "",
  };
}

function dashboardLocationHref(location = {}) {
  const normalized = normalizeDashboardLocation(location);
  return `${normalized.pathname}${normalized.search}${normalized.hash}`;
}

function getDashboardSessionStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function dashboardNavigationMemoryKey({ scope, projectKey = "", kind, key }) {
  const safeScope = scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? DASHBOARD_NAVIGATION_SCOPES.PROJECT
    : DASHBOARD_NAVIGATION_SCOPES.GLOBAL;
  const contextKey = safeScope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? encodeURIComponent(String(projectKey || ""))
    : "account";
  return `${DASHBOARD_NAVIGATION_MEMORY_PREFIX}:${safeScope}:${contextKey}:${kind}:${key}`;
}

function readDashboardNavigationMemory(context) {
  const storage = getDashboardSessionStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(dashboardNavigationMemoryKey(context));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? normalizeDashboardLocation(parsed)
      : null;
  } catch {
    return null;
  }
}

function writeDashboardNavigationMemory(context, location) {
  const storage = getDashboardSessionStorage();
  if (!storage) return false;
  try {
    storage.setItem(
      dashboardNavigationMemoryKey(context),
      JSON.stringify(normalizeDashboardLocation(location)),
    );
    return true;
  } catch {
    return false;
  }
}

function navigationProjectKey(navigation = {}) {
  return String(navigation?.project?.projectKey || "").trim();
}

function routeMatchesProjectContext(pathname, projectKey) {
  const route = resolveDashboardRouteContext(pathname);
  return Boolean(
    route.isProject &&
      projectKey &&
      String(route.projectKey) === String(projectKey)
  );
}

function navigationToolMatchesLocation(tool, location = {}) {
  if (!tool || tool.visible === false) return false;
  const normalized = normalizeDashboardLocation(location);
  if (
    tool.path &&
    isDashboardRouteActive(normalized.pathname, tool.path, tool.exact === true) &&
    navigationToolSearchMatches(tool, normalized.search)
  ) {
    return true;
  }
  return (tool.secondaryTools || []).some((child) =>
    navigationToolMatchesLocation(child, normalized),
  );
}

function navigationAreaMatchesLocation(navigation, areaKey, scope, location = {}) {
  if (scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT) {
    const projectArea = (navigation?.project?.areas || [])
      .find((area) => area.key === areaKey);
    return Boolean(projectArea?.tools?.some((tool) =>
      navigationToolMatchesLocation(tool, location),
    ));
  }

  return Boolean((navigation?.global?.tools || []).some((tool) =>
    tool.areaKey === areaKey &&
      navigationToolMatchesLocation(tool, location),
  ));
}

function memoryContextForTarget(navigation, scope, kind, key) {
  const projectKey = scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? navigationProjectKey(navigation)
    : "";
  if (scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT && !projectKey) return null;
  return { scope, projectKey, kind, key };
}

function rememberedLocationBelongsToScope(location, scope, projectKey = "") {
  const route = resolveDashboardRouteContext(location?.pathname || "");
  if (scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT) {
    return route.isProject && String(route.projectKey) === String(projectKey || "");
  }
  return route.isGlobal;
}

/**
 * Guarda solamente contexto recuperable de navegación (pathname + query + hash)
 * dentro de la pestaña actual. No redirige ni reemplaza historial: los deep links
 * y el botón Atrás/Adelante siguen siendo la autoridad de la ubicación presente.
 */
export function rememberDashboardNavigationContext(navigation = {}, location = {}) {
  const snapshot = normalizeDashboardLocation(location);
  const route = navigation?.route || resolveDashboardRouteContext(snapshot.pathname);
  const areaKey = navigation?.activeAreaKey || null;
  if (!areaKey || route.pathname !== snapshot.pathname) return false;

  const scope = route.scope;
  const projectKey = scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? navigationProjectKey(navigation)
    : "";

  // Durante la carga de un deep link puede existir todavía otro proyecto en el
  // contexto. No contaminamos su memoria hasta que el shell resuelva el proyecto
  // codificado por la URL.
  if (scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT) {
    if (!projectKey || !routeMatchesProjectContext(snapshot.pathname, projectKey)) return false;
  }

  if (!navigationAreaMatchesLocation(navigation, areaKey, scope, snapshot)) return false;

  const areaContext = memoryContextForTarget(navigation, scope, "area", areaKey);
  if (!areaContext) return false;
  let wrote = writeDashboardNavigationMemory(areaContext, snapshot);

  for (const tool of navigation?.activeToolBranch || []) {
    if (!tool?.key || tool.visible === false) continue;
    const toolContext = memoryContextForTarget(
      navigation,
      tool.scope || scope,
      "tool",
      `${areaKey}:${tool.key}`,
    );
    if (toolContext) wrote = writeDashboardNavigationMemory(toolContext, snapshot) || wrote;
  }

  return wrote;
}

/**
 * Devuelve el destino de un área sin borrar contexto recuperable. Sólo restaura
 * una URL previamente visitada si todavía pertenece a la misma área, proyecto y
 * herramientas visibles; de lo contrario usa el destino canónico de la taxonomía.
 */
export function resolveDashboardAreaNavigationTarget(
  navigation = {},
  areaOrKey = null,
  location = {},
) {
  const areas = resolveDashboardPrimaryAreas(navigation);
  const areaKey = typeof areaOrKey === "string" ? areaOrKey : areaOrKey?.key;
  const area = areas.find((candidate) => candidate.key === areaKey)
    || (typeof areaOrKey === "object" ? areaOrKey : null);
  if (!area?.path) return dashboardLocationHref(location);

  const current = normalizeDashboardLocation(location);
  if (area.active) return dashboardLocationHref(current);

  const scope = area.scope || DASHBOARD_NAVIGATION_SCOPES.GLOBAL;
  const projectKey = scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? navigationProjectKey(navigation)
    : "";
  const memoryContext = memoryContextForTarget(navigation, scope, "area", area.key);
  const remembered = area.restoreMemory === false
    ? null
    : (memoryContext ? readDashboardNavigationMemory(memoryContext) : null);

  if (
    remembered &&
    rememberedLocationBelongsToScope(remembered, scope, projectKey) &&
    navigationAreaMatchesLocation(navigation, area.key, scope, remembered)
  ) {
    return dashboardLocationHref(remembered);
  }

  return area.path;
}

/**
 * Igual que el retorno por área, pero para una herramienta concreta. Esto evita
 * que un click sobre Catálogo/Agenda/Caja, por ejemplo, borre filtros expresados
 * en la query cuando el usuario vuelve a esa herramienta durante la misma sesión.
 */
export function resolveDashboardToolNavigationTarget(
  navigation = {},
  tool = null,
  location = {},
) {
  if (!tool?.path) return dashboardLocationHref(location);

  const current = normalizeDashboardLocation(location);
  const scope = tool.scope || (
    tool.requiresProject
      ? DASHBOARD_NAVIGATION_SCOPES.PROJECT
      : DASHBOARD_NAVIGATION_SCOPES.GLOBAL
  );
  const projectKey = scope === DASHBOARD_NAVIGATION_SCOPES.PROJECT
    ? navigationProjectKey(navigation)
    : "";
  const currentInScope = rememberedLocationBelongsToScope(current, scope, projectKey);
  const currentInToolArea = Boolean(
    tool.areaKey && navigation?.activeAreaKey === tool.areaKey,
  );

  // Repetir el click sobre la herramienta realmente activa nunca debe limpiar
  // su query/hash. Varias herramientas comparten pathname y se distinguen por
  // query (CRM/Precios/Membresías), por eso el pathname no alcanza para decidir.
  if (
    tool.restoreMemory !== false &&
    currentInScope &&
    currentInToolArea &&
    tool.active
  ) {
    return dashboardLocationHref(current);
  }

  const areaKey = tool.areaKey || navigation?.activeAreaKey || "unknown";
  const memoryContext = memoryContextForTarget(
    navigation,
    scope,
    "tool",
    `${areaKey}:${tool.key || "tool"}`,
  );
  const remembered = tool.restoreMemory === false
    ? null
    : (memoryContext ? readDashboardNavigationMemory(memoryContext) : null);

  if (
    remembered &&
    rememberedLocationBelongsToScope(remembered, scope, projectKey) &&
    navigationToolMatchesLocation(tool, remembered)
  ) {
    return dashboardLocationHref(remembered);
  }

  return tool.path;
}

export function getProjectNavigationToolStarterAreas(itemKey) {
  const definition = PROJECT_NAVIGATION_TOOL_DEFINITIONS.find((item) => item.key === itemKey);
  if (!definition) return [];
  const areas = definition.progressiveStarterAreas !== undefined
    ? definition.progressiveStarterAreas
    : definition.starterAreas;
  return [...(areas || [])];
}
